import type { RefObject } from 'react';

/** Measure natural tick sizes before giving every label on a side the same size. */
export const initAxisAndGrid = (ref: RefObject<HTMLDivElement | null>): void => {
  // Root element that owns the axis layout.
  const container = ref.current;
  // Inner grid whose rail sizes are measured here.
  const layout = container?.querySelector<HTMLElement>(':scope > .axis-and-grid-layout');
  if (!layout) return;

  // Measure each axis rail independently.
  for (const side of ['left', 'right', 'top', 'bottom'] as const) {
    // Axis element for the current side.
    const axis = layout.querySelector<HTMLElement>(`:scope > .axis[data-side='${side}']`);
    if (!axis) {
      layout.style.setProperty(`--${side}-rail`, '0px');
      continue;
    }
    // Rail dimension used for tick labels on this side.
    const dimension = side === 'left' || side === 'right'
      ? 'width'
      : 'height';
    // Container holding this axis's tick labels.
    const tick_container = axis.querySelector<HTMLElement>(':scope > .html-axis-ticks');
    if (!tick_container) {
      axis.classList.remove('has-overlap');
      continue;
    }
    // Tick labels whose size and overlap need measuring.
    const ticks = [...tick_container.querySelectorAll<HTMLElement>(':scope > .html-axis-tick')];

    // Always detect overlap from unrotated labels, including after a resize.
    axis.classList.remove('has-overlap');
    // Reset previous measurements so labels can shrink as well as grow.
    for (const tick of ticks) tick.style[dimension] = 'max-content';
    // Largest natural tick size determines the shared rail label size.
    const maximum = Math.ceil(ticks.reduce((size, tick) =>
      Math.max(size, tick.getBoundingClientRect()[dimension]), 0));
    for (const tick of ticks) tick.style[dimension] = `${maximum}px`;

    // Keep the positioning container at the natural size; rotation changes visual bounds only.
    tick_container.style[dimension] = `${maximum}px`;

    // Measured bounds used to detect label intersections.
    const tick_rects = ticks.map((tick) => tick.getBoundingClientRect());
    // Whether any pair of tick labels overlaps.
    let has_overlap = false;
    // Compare each tick against later ticks until an overlap is found.
    for (let i = 0; i < tick_rects.length && !has_overlap; i++) {
      for (let j = i + 1; j < tick_rects.length; j++) {
        // Bounds of the current pair of labels.
        const rect = tick_rects[i];
        const other = tick_rects[j];
        if (rect.left < other.right && rect.right > other.left
          && rect.top < other.bottom && rect.bottom > other.top) {
          has_overlap = true;
          break;
        }
      }
    }
    axis.classList.toggle('has-overlap', has_overlap);

    // Reserve the outward extent of rotated labels in the parent axis rail.
    // Measuring from the tick container also accounts for transform-origin and translation.
    const tick_bounds = tick_container.getBoundingClientRect();
    const label_size = Math.ceil(ticks.reduce((size, tick) => {
      const bounds = tick.getBoundingClientRect();
      const extent = side === 'top' ? tick_bounds.bottom - bounds.top
        : side === 'bottom' ? bounds.bottom - tick_bounds.top
        : side === 'left' ? tick_bounds.right - bounds.left
        : bounds.right - tick_bounds.left;
      return Math.max(size, extent);
    }, maximum));

    // Optional title and its measured size.
    const title = axis.querySelector<HTMLElement>(':scope > .html-axis-title');
    const title_size = title?.getBoundingClientRect()[dimension] || 0;
    // Computed styles used to calculate the rail's required size.
    const axis_style = getComputedStyle(axis);
    const tick_style = getComputedStyle(tick_container);
    // Title styles are only needed when a title exists.
    const title_style = title
      ? getComputedStyle(title)
      : undefined;
    // Convert a CSS pixel value to a number, defaulting invalid values to zero.
    const pixels = (value: string | undefined): number => {
      // Parsed numeric portion of the CSS value.
      const number = parseFloat(value || '');
      return Number.isFinite(number) ? number : 0;
    };
    // Inset between the tick labels and the plot.
    const tick_inset = pixels(side === 'left'
      ? tick_style.right
      : side === 'right'
        ? tick_style.left
        : side === 'top'
          ? tick_style.bottom
          : tick_style.top
    );
    // Inset between the title and the plot edge.
    const title_inset = title
      ? pixels(side === 'left'
        ? title_style?.left
        : side === 'right'
          ? title_style?.marginRight
          : side === 'top'
            ? title_style?.top
            : axis_style.paddingBottom
      )
      : 0;
    // Gap between a title and tick labels.
    const gap = title && ticks.length
      ? pixels(axis_style.columnGap)
      : 0;
    // Border thickness on the plot-facing edge of this rail.
    const border = pixels(side === 'left'
      ? axis_style.borderRightWidth
      : side === 'right'
        ? axis_style.borderLeftWidth
        : side === 'top'
          ? axis_style.borderBottomWidth
          : axis_style.borderTopWidth
    );
    // Total required size of this axis rail.
    const rail_size = Math.ceil(label_size + title_size + tick_inset + title_inset + gap + border);
    // CSS value used for the grid rail.
    const size = `${rail_size}px`;
    if (side === 'bottom' && title) {
      title.style.marginTop = `${rail_size - border - title_size - title_inset}px`;
    }
    tick_container.style[dimension] = `${maximum}px`;
    axis.style.removeProperty(dimension);
    layout.style.setProperty(`--${side}-rail`, size);


  }
};
